// src/context/AuthContext.js
import { createContext, useContext, useState } from "react";
import { authLogin, authLogout } from "../services/auth.service";
import { decodeJwt } from "../utils/jwt-decoder";

type AuthContextType = {
	user: string | null;
	login: (username: string, password: string) => Promise<void>;
	logout: () => void;
	isAuthenticated: boolean;
	setTokens: (tokens: {
		accessToken: string;
		refreshToken: string;
		idToken: string;
		expiresOn: string;
	}) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: any) => {
	const [user, setUser] = useState(null);
	const [accessToken, setAccessToken] = useState(
		localStorage.getItem("accessToken")
	);
	const [refreshToken, setRefreshToken] = useState(
		localStorage.getItem("refreshToken")
	);
	const [idToken, setIdToken] = useState(localStorage.getItem("idToken"));
	const [tokenExpiration, setTokenExpiration] = useState(
		localStorage.getItem("accessTokenExpiresOn")
	);

	// Login function
	const login = async (username: string, password: string) => {
		try {
			const response = await authLogin({ username, password });
			const { accessToken, expiresOn, refreshToken, idToken } = response.data;
			setAccessToken(accessToken);
			setRefreshToken(refreshToken);
			setIdToken(idToken);
			setTokenExpiration(expiresOn);
			// setUser(user);
			localStorage.setItem("accessToken", accessToken);
			localStorage.setItem("refreshToken", refreshToken);
			localStorage.setItem("idToken", idToken);
			localStorage.setItem("tokenExpiration", expiresOn);
		} catch (error: any) {
			console.error("Login failed:", error.response?.data || error.message);
			throw error;
		}
	};

	// Logout function
	const logout = async () => {
		try {
			await authLogout(localStorage.getItem("refreshToken"));
		} catch (ex: any) {
			// The session is dropped on this device regardless; a failed revoke only
			// means the refresh token lives until it expires on the server.
			console.warn("Logout could not revoke the session on the server", ex);
		}

		setUser(null);
		setAccessToken(null);
		setRefreshToken(null);
		setIdToken(null);
		setTokenExpiration(null);
		localStorage.removeItem("accessToken");
		localStorage.removeItem("refreshToken");
		localStorage.removeItem("idToken");
		localStorage.removeItem("tokenExpiration");
	};

	// Set tokens function for SSO
	const setTokens = (tokens: {
		accessToken: string;
		refreshToken: string;
		idToken: string;
		expiresOn: string;
	}) => {
		setAccessToken(tokens.accessToken);
		setRefreshToken(tokens.refreshToken);
		setIdToken(tokens.idToken);
		setTokenExpiration(tokens.expiresOn);
		localStorage.setItem("accessToken", tokens.accessToken);
		localStorage.setItem("refreshToken", tokens.refreshToken);
		localStorage.setItem("idToken", tokens.idToken);
		localStorage.setItem("tokenExpiration", tokens.expiresOn);
		// Decode and set user info from token
		const decodedToken = decodeJwt(tokens.accessToken);
		if (decodedToken) {
			setUser(decodedToken.email || decodedToken.sub);
		}
	};

	// Check authentication status
	const isAuthenticated = !!accessToken;

	return (
		<AuthContext.Provider
			value={{ user, login, logout, isAuthenticated, setTokens }}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);

	// If the context is null, throw an error to ensure it's only used within an AuthProvider
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}

	return context;
};

export const IsSuperAdmin = () => {
	return (
		decodeJwt(localStorage.getItem("accessToken") ?? "")?.role == "SuperAdmin"
	);
};

export const IsAdmin = () => {
	return decodeJwt(localStorage.getItem("accessToken") ?? "")?.role == "Admin";
};

export const CurrentUserId = (): string | undefined =>
	decodeJwt(localStorage.getItem("accessToken") ?? "")?.sub;
